import { MutableRefObject, useEffect, useCallback, useMemo, useState } from 'react';

import _debounce from 'lodash/debounce';

/**
 * Given a ref in the dom, return its computed CSS properties and listen
 * for window resize events for updates. Scroll height and width are returned
 * as well.
 */
export const useComputedStyle = <T extends HTMLElement = HTMLDivElement>(
  ref: MutableRefObject<T | null>,

  // An optional parameter to force re-evaluation
  // Change this value and the dimensions will be re-computed
  forceUpdate?: string
): Partial<CSSStyleDeclaration> => {
  const [computedStyle, setComputedStyle] = useState<Partial<CSSStyleDeclaration>>({});

  const fetchComputedStyle = useCallback(
    (): Partial<CSSStyleDeclaration> => (ref?.current ? getComputedStyle(ref?.current) : {}),
    [ref]
  );

  const handleWindowResize = useMemo(() => {
    return _debounce(() => {
      setComputedStyle(fetchComputedStyle());
    }, 500);
  }, [fetchComputedStyle]);

  useEffect(() => {
    setComputedStyle(fetchComputedStyle());

    window.addEventListener('resize', handleWindowResize);

    return () => {
      window.removeEventListener('resize', handleWindowResize);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchComputedStyle, handleWindowResize, ref.current, forceUpdate]);

  return computedStyle;
};
